import { faMeta } from '@fortawesome/free-brands-svg-icons';
import { faRectangleAd } from '@fortawesome/pro-regular-svg-icons';
import type { IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, DisplayCreative, IDimensionDefinition } from '@feathr/blackbox';
import { getAspectRatioNotation, ValidDimensions } from '@feathr/blackbox';
import { CreativeThumbnail, RequirementsCard } from '@feathr/components';

import { useCreativeRequirementsCard } from './CreativeRequirementsCard.hooks';
import {
  getDisplayMostPopularKeys,
  getDisplayRemainingDimensions,
  hasMetaRemainingDimensions,
  isDisplayDimensionComplete,
  metaMostPopularKeys,
  metaRemainingKeys,
} from './CreativeRequirementsCard.utils';
import { MetaDimensionItem } from './MetaDimensionItem';

import * as styles from './CreativeRequirementsCard.css';

interface IProps {
  campaign: Campaign;
  creatives?: IObservableArray<DisplayCreative>;
}

function CreativeRequirementsCard({ campaign, creatives }: Readonly<IProps>): JSX.Element | null {
  const { t } = useTranslation();
  const {
    isVisible,
    usesDisplayChannel,
    usesMetaChannel,
    displayExpanded,
    metaExpanded,
    handleViewAll,
    handleToggleDisplay,
    handleToggleMeta,
  } = useCreativeRequirementsCard(campaign);

  const displayMostPopularKeys = getDisplayMostPopularKeys(campaign);
  const displayRemainingDimensions = getDisplayRemainingDimensions(campaign);
  const hasDisplayRemainingDimensions = displayRemainingDimensions.length > 0;

  const showsDisplayExpand = usesDisplayChannel && hasDisplayRemainingDimensions;
  const showsMetaExpand = usesMetaChannel && hasMetaRemainingDimensions;
  const hasAnyExpandableSection = showsDisplayExpand || showsMetaExpand;

  if (!isVisible) {
    return null;
  }

  return (
    <RequirementsCard
      header={
        <RequirementsCard.Header
          // Shows "View less" only when every section is expanded; if any is collapsed, shows "View all".
          isAllExpanded={displayExpanded && metaExpanded}
          onViewAll={hasAnyExpandableSection ? handleViewAll : undefined}
          title={t('Standard Sizes')}
        />
      }
    >
      {usesDisplayChannel && (
        <RequirementsCard.Section
          description={t('Add at least one of the following sizes.')}
          icon={faRectangleAd}
          title={t('Display Ads')}
        >
          <RequirementsCard.Specs
            imageFormats={['JPG', 'PNG', 'GIF']}
            imageMaxSize={'30MB'}
            videoCodec={'H.264 recommended'}
            videoFormats={['MP4', 'WebM']}
          />
          <RequirementsCard.MostPopular>
            {displayMostPopularKeys.map((key) => {
              const dimension = ValidDimensions[key];
              if (!dimension) {
                return null;
              }
              const { name, width, height } = dimension;
              return (
                <RequirementsCard.Item
                  aspectRatio={getAspectRatioNotation(width / height)}
                  dimensions={key}
                  itemName={name}
                  key={key}
                  thumbnail={
                    <CreativeThumbnail
                      height={height}
                      isValidated={
                        creatives ? isDisplayDimensionComplete(creatives, width, height) : false
                      }
                      variant={'display'}
                      width={width}
                    />
                  }
                />
              );
            })}
          </RequirementsCard.MostPopular>
          {hasDisplayRemainingDimensions && (
            <RequirementsCard.ShowMore isExpanded={displayExpanded} onToggle={handleToggleDisplay}>
              {displayRemainingDimensions.map((dimension: IDimensionDefinition) => {
                const { name, width, height, spec } = dimension;
                const key = `${width}x${height}`;
                return (
                  <RequirementsCard.Item
                    aspectRatio={getAspectRatioNotation(width / height)}
                    dimensions={key}
                    itemName={name}
                    key={key}
                    thumbnail={
                      <CreativeThumbnail
                        height={height}
                        isValidated={
                          creatives ? isDisplayDimensionComplete(creatives, width, height) : false
                        }
                        isVideo={spec.includes('video')}
                        variant={'display'}
                        width={width}
                      />
                    }
                  />
                );
              })}
            </RequirementsCard.ShowMore>
          )}
        </RequirementsCard.Section>
      )}

      {usesMetaChannel && (
        <RequirementsCard.Section
          description={t('Add at least one square creative.')}
          icon={faMeta}
          iconClassName={styles.metaIcon}
          title={t('Meta Creatives')}
        >
          <RequirementsCard.Specs
            imageFormats={['JPG', 'PNG']}
            imageMaxSize={'20MB'}
            videoCodec={'H.264 recommended'}
            videoFormats={['MP4', 'GIF']}
            videoMaxSize={'1GB'}
          />
          <RequirementsCard.MostPopular>
            {metaMostPopularKeys.map((key) => (
              <MetaDimensionItem creatives={creatives} dimensionKey={key} key={key} />
            ))}
          </RequirementsCard.MostPopular>
          {hasMetaRemainingDimensions && (
            <RequirementsCard.ShowMore isExpanded={metaExpanded} onToggle={handleToggleMeta}>
              {metaRemainingKeys.map((key) => (
                <MetaDimensionItem creatives={creatives} dimensionKey={key} key={key} />
              ))}
            </RequirementsCard.ShowMore>
          )}
        </RequirementsCard.Section>
      )}
    </RequirementsCard>
  );
}

export default observer(CreativeRequirementsCard);
